import { Box, Button, Container, Stack, Step, StepLabel, Stepper, useMediaQuery } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { ReportPeriodContext } from '../../../../../context/report-periods/report-periods'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EmissionsEmailCampaignScheduleForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-schedule-form'
import { CheckCircleOutline, NavigateNext } from '@mui/icons-material'
import { EmissionsEmailCampaignRecipientsForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-recipients-form'
import { EmissionsEmailCampaignContentForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-content-form'
import EmailCampaignService from '../../../../../modules/survey/shared-services/email-campaign/email-campaign.service'
import { IPostEmailCampaign } from '../../../../../modules/survey/shared-services/email-campaign/email-campaign.model'
import gaiaTheme from '../../../../../root-components/theme'
import { EmailCampaignEmployeeCommuteForm } from '../../../../../modules/survey/components/employee/email-campaign-employee-commute-form'
import { PageOuterContainer } from '../../../../../root-components/layouts/page-outer-container'
import { EmissionsEmailCampaignReviewForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-review-form'
import { PageHeading } from '../../../../presentation-components/display-elements-components/page-heading'
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked';
import { useReportPeriodWatcher } from '../../../../../hooks/use-report-period-watcher'
import { InformationBox } from '../../../../presentation-components/miscellaneous-components/information-box'
import { IconName } from '../../../../../shared/interfaces/icon-picker.model'
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications'

export const EmissionsEmailCampaignForm = () => {
  const navigate = useNavigate()
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportPeriodWatcher = useReportPeriodWatcher({ redirectUrl: `/emissions/surveys/employee` })
  //Services
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext);
  const emailCampaignService = new EmailCampaignService();
  //Hooks 
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //State
  const [activeStep, setActiveStep] = React.useState(0);
  const [campaignId, setCampaignId] = useState<string>()
  const [newSurvey, setNewSurvey] = useState<IPostEmailCampaign>({
    name: '',
    emailFrequency: '',
    campaignType: id === 'employee' ? 'EMPLOYEE_COMMUTE_EMAIL' : '',
    reminderAfterDays: undefined,
    sendDate: '',
    sendTime: '',
    tokenExpiryDays: 30,
    reportPeriodId: '',
    distributionListId: [],
    includeProjects: false,
  })
  //Loading State
  const [isSubmitted, setIsSubmitted] = useState<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Variables
  const steps = [
    'Schedule',
    'Content',
    'Recipients',
    'Review',
  ];

  useEffect(() => {
    if (reportPeriodContext.lastSelectedReportId) {
      setNewSurvey({ ...newSurvey, reportPeriodId: reportPeriodContext.lastSelectedReportId })
    }
  }, [reportPeriodContext.lastSelectedReportId])

  const handleNextValidation = () => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and therefore a new survey cannot be created`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      setIsSubmitted(isSubmitted + 1)
      if (activeStep === 3) {
        navigate(`/emissions/surveys/employee/overview?campaignId=${campaignId}`)
      }
    }
  }

  const handleNextGeneral = () => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and therefore a new survey cannot be created`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleNextRecipient = (values: string[]) => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and therefore a new survey cannot be created`, buttonCopy: 'Ok', hideCancelButton: true })
    } else if (!campaignId) {
      if (activeStep === 2 && newSurvey.emailFrequency && newSurvey.sendDate && newSurvey.sendTime) {
        emailCampaignService.createEmailCampaign({
          ...newSurvey,
          distributionListId: values,
        })
          .then((response) => {
            searchParams.set("campaignId", response.id!);
            setSearchParams(searchParams)
            setCampaignId(response.id)
          })
          .catch(err => {
            if (err.response.data.code === 1) {
              userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
            }
            console.log(err)
          })
      }
    } else {
      if (activeStep === 2 && newSurvey.emailFrequency && newSurvey.sendDate && newSurvey.sendTime) {
        emailCampaignService.editEmailCampaign(campaignId, {
          ...newSurvey,
          distributionListId: values,
        })
          .then((response) => {
            console.log(response)
          })
          .catch(err => {
            if (err.response.data.code === 1) {
              userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
            }
            console.log(err)
          })
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };


  return (
    <PageOuterContainer>
      {id && <PageHeading
        title={`Create New ${id.charAt(0).toUpperCase() + id.slice(1).toLowerCase()}`}
        includeBackButton={true}
        backButtonLink={`/emissions/surveys/${id}`}
        backButtonText={`Back to ${id} surveys`}
        hasTabs={true}
      />}
      {isReportPeriodLocked ? (
        <Stack sx={{ mt: 2 }}>
          <InformationBox
            text={`A new survey cannot be created because the selected reporting period is locked`}
            icon={IconName.ErrorOutline}
          />
        </Stack>) :
        (
          <Box sx={{ width: '100%' }}>
            {!isMobileMode && <Stack display='flex' justifyContent='center' alignItems='center'>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, mt: 6, width: '50%' }}>
                {steps.map((label) => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: {
                    optional?: React.ReactNode;
                  } = {};
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Stack>}
            {activeStep !== steps.length &&
              <React.Fragment>
                <Container maxWidth='md'>
                  {activeStep === 0 &&
                    <EmissionsEmailCampaignScheduleForm
                      newSurvey={newSurvey}
                      handleNext={handleNextGeneral}
                      isSubmitted={isSubmitted}
                      setNewSurvey={setNewSurvey}
                    />
                  }
                  {activeStep === 1 &&
                    <EmissionsEmailCampaignContentForm
                      newSurvey={newSurvey}
                      handleNext={handleNextGeneral}
                      isSubmitted={isSubmitted}
                      setNewSurvey={setNewSurvey}
                    />
                  }
                  {activeStep === 2 &&
                    <EmissionsEmailCampaignRecipientsForm
                      newSurvey={newSurvey}
                      handleNext={handleNextRecipient}
                      isSubmitted={isSubmitted}
                      setNewSurvey={setNewSurvey}
                    />
                  }
                  {activeStep === 3 &&
                    <>
                      <Stack sx={{ mb: 2 }}>
                        <EmissionsEmailCampaignReviewForm />
                      </Stack>
                      <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', pl: isMobileMode ? 4 : 10, pr: isMobileMode ? 4 : 10, pt: isMobileMode ? 2 : 4, pb: isMobileMode ? 2 : 4, mb: 4 }}>
                        <EmailCampaignEmployeeCommuteForm isDemonstrationPage={true} includeProjects={newSurvey?.includeProjects} />
                      </Box>
                    </>
                  }
                  <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3 }}>
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button onClick={handleNextValidation}>
                      {activeStep === steps.length - 1 ?
                        'Save' : 'Continue'}
                      {activeStep === steps.length - 1 && <CheckCircleOutline sx={{ ml: 1, fontSize: '1.25rem' }} />}
                      {activeStep < steps.length - 1 && <NavigateNext sx={{ ml: 1 }} />}
                    </Button>
                  </Box>
                </Container>
              </React.Fragment>
            }
          </Box>
        )
      }
    </PageOuterContainer>
  )
}